import { fontBerkeleyMono } from "@/assets";
import { injectGlobal } from "@/domains/emotion";

export const ATLAS_FONT_FAMILY_ATTRIBUTE_STRING = `-apple-system, system-ui, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", sans-serif`;
export const BERKELEY_MONO_FONT_FAMILY = `Berkeley Mono Variable, monospace`;

export const _injectGlobalProviders = () => {
  injectGlobal({
    "@font-face": {
      fontFamily: "Berkeley Mono Variable",
      fontStyle: "normal",
      src: `url("${fontBerkeleyMono}") format("woff2")`,
    },
  });

  injectGlobal({
    "*:focus-visible": {
      // Avoid blue outline on modals and other elements.
      outline: "none",
    },

    html: {
      textRendering: "optimizeLegibility",
      MozOsxFontSmoothing: "grayscale",
      WebkitFontSmoothing: "antialiased",
      backgroundColor: "#eeeeee",
    },

    body: {
      margin: 0,
      fontSize: "14px",
      lineHeight: "21px",
      backgroundColor: "#eeeeee",
    },

    "input, textarea, .radix-themes input, .radix-themes textarea": {
      border: "1px solid rgb(0, 0, 0) !important",
      backgroundColor: "rgb(255, 255, 255) !important",
      boxShadow: "rgb(221, 221, 221) 2px 2px 0px 0px inset !important",
      padding: "7px 5.6px 4.2px 5.6px",
      fontSize: "14px",
      lineHeight: "21px",
      color: "rgb(0, 0, 0)",
      outline: "none",
      borderRadius: "0",
      fontFamily: ATLAS_FONT_FAMILY_ATTRIBUTE_STRING,

      "&:focus": {
        boxShadow: "rgb(187, 187, 187) 2px 2px 0px 0px inset !important",
        backgroundColor: "rgb(250, 250, 250) !important",
      },
    },

    // Basic button reset - will be overridden by component-specific styling
    button: {
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      outline: "none",
      userSelect: "none",
      fontFamily: "inherit",
      fontSize: "inherit",
      lineHeight: "inherit",
      color: "inherit",
    },

    // Focus states
    "input:focus, textarea:focus, .radix-themes input:focus, .radix-themes textarea:focus": {
      boxShadow: "rgb(221, 221, 221) 2px 2px 0px 0px inset, 0 0 0 1px rgb(0, 0, 0)",
    },

    submit: {
      border: "none",
    },

    a: {
      textDecoration: "none",
    },

    h1: {
      fontWeight: "var(--font-weight-medium)",
      marginTop: "0",
      marginBottom: "1rem",
    },

    "*": {
      boxSizing: "border-box",
      fontFamily: `${ATLAS_FONT_FAMILY_ATTRIBUTE_STRING} !important`,
      fontWeight: "unset",
    },

    "table, table *, th, td, code, pre, .monospace": {
      fontFamily: `${BERKELEY_MONO_FONT_FAMILY} !important`,
    },
  });

  injectGlobal({
    ".atlas-dark-mode": {
      filter: "invert(1) hue-rotate(180deg)",
      backgroundColor: "#111111",
      colorScheme: "dark",
    },
    ".atlas-dark-mode img, .atlas-dark-mode video, .atlas-dark-mode picture, .atlas-dark-mode iframe, .atlas-dark-mode canvas":
      {
        filter: "invert(1) hue-rotate(180deg)",
      },
  });

  /**
   * Theme generated based on our new style guide and:
   * - https://www.radix-ui.com/colors/custom
   *
   * Primary Color - Ink
   * Grey Color - Canvas
   * Background Color - Paper
   */
  injectGlobal(`
    @supports (color: color(display-p3 1 1 1)) {
    @media (color-gamut: p3) {
      :root, .light, .light-theme {
        --violet-1: oklch(99.2% 0.0079 279.7);
        --violet-2: oklch(98.3% 0.0147 279.7);
        --violet-3: oklch(95.6% 0.0268 279.7);
        --violet-4: oklch(93.2% 0.0362 279.7);
        --violet-5: oklch(90.9% 0.0476 279.7);
        --violet-6: oklch(88.5% 0.056 279.7);
        --violet-7: oklch(85.3% 0.0691 279.7);
        --violet-8: oklch(79.2% 0.0936 279.7);
        --violet-9: oklch(19.4% 0.0656 279.7);
        --violet-10: oklch(29.6% 0.0656 279.7);
        --violet-11: oklch(50.5% 0.0851 279.7);
        --violet-12: oklch(24.6% 0.0656 279.7);
  
        --violet-a1: color(display-p3 0.0235 0.2667 1 / 0.016);
        --violet-a2: color(display-p3 0.0196 0.2667 0.8784 / 0.032);
        --violet-a3: color(display-p3 0.0078 0.1176 0.9451 / 0.071);
        --violet-a4: color(display-p3 0.0039 0.0784 0.9294 / 0.106);
        --violet-a5: color(display-p3 0.0078 0.0902 0.9176 / 0.142);
        --violet-a6: color(display-p3 0.0039 0.0941 0.9373 / 0.177);
        --violet-a7: color(display-p3 0.0039 0.0745 0.8784 / 0.224);
        --violet-a8: color(display-p3 0.0039 0.0667 0.8392 / 0.31);
        --violet-a9: color(display-p3 0.0039 0 0.1333 / 0.942);
        --violet-a10: color(display-p3 0 0.0039 0.1686 / 0.848);
        --violet-a11: color(display-p3 0 0.0196 0.3216 / 0.64);
        --violet-a12: color(display-p3 0 0.0039 0.149 / 0.895);
  
        --violet-contrast: #fff;
        --violet-surface: color(display-p3 0.9608 0.9725 1 / 0.8);
        --violet-indicator: oklch(19.4% 0.0656 279.7);
        --violet-track: oklch(19.4% 0.0656 279.7);
      }
      }
    }

    :root, .light, .light-theme {
      --gray-1: #eeeeee;
      --gray-2: #e8e8e8;
      --gray-3: #e0e0e0;
      --gray-4: #e5e5e5;
      --gray-5: #d4d4d4;
      --gray-6: #a3a3a3;
      --gray-7: #737373;
      --gray-8: #525252;
      --gray-9: #404040;
      --gray-10: #262626;
      --gray-11: #171717;
      --gray-12: #000000;

      --gray-a1: #00000008;
      --gray-a2: #0000000a;
      --gray-a3: #0000000f;
      --gray-a4: #0000001a;
      --gray-a5: #0000002b;
      --gray-a6: #0000005c;
      --gray-a7: #0000008c;
      --gray-a8: #000000ad;
      --gray-a9: #000000bf;
      --gray-a10: #000000d9;
      --gray-a11: #000000e8;
      --gray-a12: #000000ff;

      --gray-contrast: #FFFFFF;
      --gray-surface: #ffffffcc;
      --gray-indicator: #404040;
      --gray-track: #404040;

      [data-accent-color='violet'] {
        --accent-1: var(--gray-1);
        --accent-2: var(--gray-2);
        --accent-3: var(--gray-3);
        --accent-4: var(--gray-4);
        --accent-5: var(--gray-5);
        --accent-6: var(--gray-6);
        --accent-7: var(--gray-7);
        --accent-8: var(--gray-8);
        --accent-9: var(--gray-9);
        --accent-10: var(--gray-10);
        --accent-11: var(--gray-11);
        --accent-12: var(--gray-12);
        --accent-a1: var(--gray-a1);
        --accent-a2: var(--gray-a2);
        --accent-a3: var(--gray-a3);
        --accent-a4: var(--gray-a4);
        --accent-a5: var(--gray-a5);
        --accent-a6: var(--gray-a6);
        --accent-a7: var(--gray-a7);
        --accent-a8: var(--gray-a8);
        --accent-a9: var(--gray-a9);
        --accent-a10: var(--gray-a10);
        --accent-a11: var(--gray-a11);
        --accent-a12: var(--gray-a12);
        --accent-contrast: var(--violet-contrast);
        --accent-surface: var(--violet-surface);
        --accent-indicator: var(--violet-indicator);
        --accent-track: var(--violet-track);
      }
    }

    @supports (color: color(display-p3 1 1 1)) {
      @media (color-gamut: p3) {
        :root, .light, .light-theme {
          --gray-1: oklch(94.5% 0 0);
          --gray-2: oklch(92.5% 0 0);
          --gray-3: oklch(89.4% 0 0);
          --gray-4: oklch(91.3% 0 0);
          --gray-5: oklch(85.9% 0 0);
          --gray-6: oklch(69.5% 0 0);
          --gray-7: oklch(53.8% 0 0);
          --gray-8: oklch(41.1% 0 0);
          --gray-9: oklch(32.7% 0 0);
          --gray-10: oklch(23.5% 0 0);
          --gray-11: oklch(16.7% 0 0);
          --gray-12: oklch(0% 0 0);

          --gray-a1: color(display-p3 0 0 0 / 0.031);
          --gray-a2: color(display-p3 0 0 0 / 0.039);
          --gray-a3: color(display-p3 0 0 0 / 0.059);
          --gray-a4: color(display-p3 0 0 0 / 0.102);
          --gray-a5: color(display-p3 0 0 0 / 0.169);
          --gray-a6: color(display-p3 0 0 0 / 0.361);
          --gray-a7: color(display-p3 0 0 0 / 0.549);
          --gray-a8: color(display-p3 0 0 0 / 0.678);
          --gray-a9: color(display-p3 0 0 0 / 0.749);
          --gray-a10: color(display-p3 0 0 0 / 0.851);
          --gray-a11: color(display-p3 0 0 0 / 0.91);
          --gray-a12: color(display-p3 0 0 0 / 1);

          --gray-contrast: #FFFFFF;
          --gray-surface: color(display-p3 1 1 1 / 80%);
          --gray-indicator: oklch(32.7% 0 0);
          --gray-track: oklch(32.7% 0 0);
        }
      }
    }

    :root, .light, .light-theme, .radix-themes {
      --color-background: #eeeeee;
    }
  `);

  injectGlobal({
    ".radix-themes": {
      "--cursor-button": "pointer",
      "--cursor-checkbox": "pointer",
      "--cursor-disabled": "default",
      "--cursor-link": "pointer",
      "--cursor-menu-item": "pointer",
      "--cursor-radio": "pointer",
      "--cursor-slider-thumb": "grab",
      "--cursor-slider-thumb-active": "grabbing",
      "--cursor-switch": "pointer",
      "--default-font-family": `${ATLAS_FONT_FAMILY_ATTRIBUTE_STRING} !important`,
      "--radius-factor": "0",
      "--color-background": "var(--gray-0)",
    },
  });

  injectGlobal({
    ".rt-TextFieldRoot": {
      "--text-field-focus-color": "none",
      boxShadow: "none !important",
    },
  });
};
